// Angular Imports
import { BreakpointObserver } from '@angular/cdk/layout';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

// Lodash

// My Account Imports
import { env } from '@amfam/environment';
// Store
import { PolicyDocumentsSelectors, PolicySelectors } from '@amfam/policy/data-access';
import { LifePolicy, PolicyAnalytics, PolicyDetails, PolicyDocument } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { CopyService, ScrollService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

@Component({
  selector: 'ds-life',
  templateUrl: './life.component.html',
  styleUrls: ['../shared/policy-shared.scss'],
  standalone: false
})
export class LifeComponent implements OnInit, AfterViewInit, OnDestroy {
  public policy$: Observable<LifePolicy>;
  public amfamPhoneNumber: string;
  public lifeBillingNumber: string;
  public policyErrorList: Array<ErrorObj> = [];
  public policyDocuments$: Observable<PolicyDocument[]>;
  public showNewBadge: boolean;
  newTemplate = this.featureFlagService.isEnabled('life_policy_new_template');
  documents = this.copyService.getCopy('shared.documents');
  openDocuments = false;
  private stop$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private analyticsFacade: AnalyticsFacade,
    private store: Store,
    private spinner: LoadingSpinnerService,
    private copyService: CopyService,
    private featureFlagService: FeatureFlagService,
    private breakpointObserver: BreakpointObserver,
    private scrollService: ScrollService
  ) {}

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  ngOnInit() {
    this.spinner.start();
    this.openDocs();
    this.amfamPhoneNumber = env.lifeNumberLink.split(':')[1];
    this.lifeBillingNumber = env.lifeBillingLink.split(':')[1];

    this.policy$ = this.route.params.pipe(
      filter(params => !!params),
      take(1),
      switchMap(
        params =>
          <Observable<LifePolicy>>(
            this.store.pipe(select(PolicySelectors.selectActivePolicies)).pipe(
              filter(policies => !!policies.length),
              first(),
              map(policies => {
                const matchedPolicy = policies.find(policy => policy.policyNumber === params.id);
                if (!matchedPolicy) {
                  this.policyErrorList = new Array(this.getPoliciesError(this.copyService));
                  return null;
                }
                return matchedPolicy;
              })
            )
          )
      ),
      tap(() => {
        this.spinner.stop();
        const policyDetailsCopy: string = this.copyService.getCopy('policy.policyDetails');
        this.pageTitle.set(policyDetailsCopy);
        this.dockingBarService.registerHeading(policyDetailsCopy);
      })
    );
    this.showNewBadge = false;
    this.policyDocuments$ = combineLatest([
      this.policy$,
      this.store.select(PolicyDocumentsSelectors.selectPolicyDocuments)
    ]).pipe(
      takeUntil(this.stop$),
      filter(([policy, documents]) => !!policy && !!documents.length),
      map(([policy, documents]) =>
        documents.filter(policyDocument => {
          if (policyDocument.isNew && policyDocument.policyNumber === policy.policyNumber) {
            this.showNewBadge = true;
          }
          return policyDocument.policyNumber === policy.policyNumber;
        })
      )
    );
  }

  openDocs() {
    this.route.queryParamMap.pipe(takeUntil(this.stop$)).subscribe(queryParams => {
      const openAccordion = queryParams.get('openAccordion');
      if (openAccordion === 'documents') this.openDocuments = true;
    });
  }

  ngAfterViewInit() {
    this.analyticsFacade.trackPage(PolicyAnalytics.pageLifePolicyDetailAnalytic);
  }

  /**
   * @description Function which fetches the error from the copy service and returns it in the error obj format.
   */
  public getPoliciesError(copyService: CopyService): ErrorObj {
    return {
      errorMessage: copyService.getCopy('policy.defaultErrorMessage'),
      errorLevel: ErrorLevels.HIGH
    };
  }

  onCoverageDocumentsClicked() {
    this.analyticsFacade.trackButtonClick(PolicyDetails.jumpLinkCoverageDocuments);

    if (this.isSmallScreen()) this.scrollService.scrollToElement('documentsCard', 85);
    else this.scrollService.scrollToElement('documentsCard', 215);
  }

  gotodocuments() {
    if (this.openDocuments) {
      if (this.isSmallScreen()) this.scrollService.scrollToElement('documentsCard', 55);
      else this.scrollService.scrollToElement('documentsCard', 215);
    }
  }

  private isSmallScreen() {
    return this.breakpointObserver.isMatched('(max-width: 480px) and (orientation: portrait)');
  }
}
